import { Divider, IFormRender, Justify, Label, Row, TextFormField } from '@busybusy/webapp-react-ui';
import EquipmentInspectionChecklistPickerFormField from 'components/domain/equipment/EquipmentInspectionChecklistPickerFormField/EquipmentInspectionChecklistPickerFormField';
import { t } from 'utils/localize';
import v_integers_only from 'utils/validations/v_number_integers_only';
import v_require_conditional from 'utils/validations/v_require_conditional';
import { RoutineInspectionFormFields, buildRoutineFieldName, getRoutineRowCount } from './routineInspectionFormUtils';

export interface IRoutineInspectionFieldsProps<T extends RoutineInspectionFormFields> {
  form: IFormRender<T>;
}

/**
 * Dynamic list of routine-inspection rows (checklist + recurring-day interval) shared by the
 * equipment create and edit forms. Renders at least EQUIPMENT_MAX_ROUTINE_INSPECTIONS rows, growing
 * to show any extra existing routines beyond the cap (getRoutineRowCount), bound to flat form fields
 * named via buildRoutineFieldName; the interval is required only once a row's checklist is selected,
 * and empty rows are ignored on submit (getRoutineItemsFromFormData). Submission past the cap is
 * blocked by the parent form (exceedsRoutineInspectionLimit). The surrounding section
 * header/wrapper is left to the parent form.
 */
export default function RoutineInspectionFields<FormType extends RoutineInspectionFormFields>(
  props: IRoutineInspectionFieldsProps<FormType>
) {
  const { form } = props;

  return (
    <>
      <Row justify={Justify.SPACE_BETWEEN}>
        <Label>{t('Inspection')}</Label>
        <Label>{t('Interval (Days)')}</Label>
      </Row>
      <Divider className={'mb-4'} />
      {Array.from({ length: getRoutineRowCount(form.state.data) }, (_, index) => {
        const rowNumber = index + 1;
        const checklistFieldName = buildRoutineFieldName('checklistId', rowNumber);
        const intervalFieldName = buildRoutineFieldName('interval', rowNumber);
        return (
          <Row key={rowNumber} justify={Justify.SPACE_BETWEEN}>
            <EquipmentInspectionChecklistPickerFormField
              name={checklistFieldName}
              form={form}
              className={'routine-checklist-picker'}
            />
            <TextFormField
              name={intervalFieldName}
              form={form}
              className={'routine-interval-field'}
              validations={[
                {
                  validation: v_require_conditional,
                  args: { conditionalValue: form.state.data[checklistFieldName] },
                },
                { validation: v_integers_only },
              ]}
            />
          </Row>
        );
      })}
    </>
  );
}
